import toast from 'react-hot-toast';

const operations = new Map();
const pendingResult = new Map();
const closedOperations = new Set();

const SUCCESS_DURATION = 4000;
const ERROR_DURATION = 6000;
const PROCESSING_TIMEOUT = 30000;
const RESULT_TTL = 10000;

function cleanupOperation(opId) {
  const op = operations.get(opId);
  if (!op) return;

  if (op.timeoutId) clearTimeout(op.timeoutId);
  operations.delete(opId);
}

function cachePendingResult(opId, result) {
  const timeoutId = setTimeout(() => {
    pendingResult.delete(opId);
  }, RESULT_TTL);

  pendingResult.set(opId, { ...result, timeoutId });
}

function consumePendingResult(opId) {
  const result = pendingResult.get(opId);
  if (!result) return null;

  clearTimeout(result.timeoutId);
  pendingResult.delete(opId);
  return result;
}

function clearClosedOperation(opId) {
  closedOperations.delete(opId);
}

function markClosedOperation(opId) {
  closedOperations.add(opId);
}

function isClosedOperation(opId) {
  return closedOperations.has(opId);
}

export function beginToast(opId, message = 'Processing your swap…') {
  if (!opId) return null;

  // Treat this as a fresh lifecycle start for this opId.
  clearClosedOperation(opId);

  const earlyResult = consumePendingResult(opId);
  if (earlyResult) {
    if (earlyResult.type === 'success') {
      toast.success(earlyResult.message, { duration: SUCCESS_DURATION });
    } else {
      toast.error(earlyResult.message, { duration: ERROR_DURATION });
    }
    return null;
  }

  const existing = operations.get(opId);
  if (existing) {
    toast.loading(message, { id: existing.toastId, duration: Infinity });
    return existing.toastId;
  }

  const toastId = toast.loading(message, { duration: Infinity });

  const timeoutId = setTimeout(() => {
    const current = operations.get(opId);
    if (!current) return;

    toast.dismiss(current.toastId);
    cleanupOperation(opId);
    markClosedOperation(opId);
  }, PROCESSING_TIMEOUT);

  operations.set(opId, {
    toastId,
    timeoutId,
    startedAt: Date.now(),
  });

  return toastId;
}

export function resolveToast(opId, message = 'Deposit sent. Confirming…') {
  if (!opId) return;

  if (isClosedOperation(opId)) {
    return;
  }

  const op = operations.get(opId);

  if (op) {
    toast.success(message, {
      id: op.toastId,
      duration: SUCCESS_DURATION,
    });
    cleanupOperation(opId);
    return;
  }

  cachePendingResult(opId, { type: 'success', message });
}

export function rejectToast(opId, message = 'Swap failed') {
  if (!opId) return;

  if (isClosedOperation(opId)) {
    return;
  }

  const op = operations.get(opId);

  if (op) {
    toast.error(message, {
      id: op.toastId,
      duration: ERROR_DURATION,
    });
    cleanupOperation(opId);
    return;
  }

  cachePendingResult(opId, { type: 'error', message });
}

export function dismissToast(opId) {
  if (!opId) return;

  const op = operations.get(opId);
  if (op) {
    toast.dismiss(op.toastId);
    cleanupOperation(opId);
    markClosedOperation(opId);
  }

  const cached = pendingResult.get(opId);
  if (cached) {
    clearTimeout(cached.timeoutId);
    pendingResult.delete(opId);
  }
}

export function updateToast(opId, message) {
  if (!opId) return;

  if (isClosedOperation(opId)) {
    return;
  }

  const op = operations.get(opId);
  if (op) {
    toast.loading(message, { id: op.toastId, duration: Infinity });
  }
}

export function resetToastFlow(opId) {
  if (!opId) return;

  clearClosedOperation(opId);

  const cached = pendingResult.get(opId);
  if (cached) {
    clearTimeout(cached.timeoutId);
    pendingResult.delete(opId);
  }

  const op = operations.get(opId);
  if (op) {
    toast.dismiss(op.toastId);
    cleanupOperation(opId);
  }
}
